import {
  pullRequestNumberFromUrl as forgePullRequestNumber,
  type PullRequestState,
  type PullRequestSummary,
} from '@crowdex/workspace-protocol';

export function pullRequestState(input: {
  draft?: boolean;
  merged?: boolean;
  state?: string;
}): PullRequestState {
  if (input.merged === true) return 'merged';
  if (input.state?.toLowerCase() === 'closed') return 'closed';
  if (input.draft === true) return 'draft';
  return 'open';
}

export function pullRequestLabel(state: PullRequestState): string {
  if (state === 'draft') return 'Draft PR';
  if (state === 'merged') return 'Merged';
  if (state === 'closed') return 'Closed';
  return 'Open PR';
}

export function pullRequestStateClass(state: PullRequestState): string {
  if (state === 'merged') return 'text-mode-team';
  if (state === 'closed') return 'text-destructive';
  if (state === 'open') return 'text-desktop-success';
  return 'text-muted-foreground';
}

/** Keep the last known pull request when a transient provider lookup fails on the same branch. */
export function pullRequestForBranch(
  live: PullRequestSummary | null,
  cached: PullRequestSummary | null | undefined,
  branch: string | null,
): PullRequestSummary | null {
  if (live !== null) return live;
  if (cached === null || cached === undefined || branch === null) return null;
  return cached.headBranch.trim() === branch.trim() ? cached : null;
}

export function pullRequestNumberFromUrl(url: string | null): number | null {
  if (url === null) return null;
  const match = url.match(
    /^https:\/\/[^/]+\/[^/]+\/[^/]+\/(?:pull|pull-requests|merge_requests)\/(\d+)(?:\/.*)?$/i,
  );
  if (match?.[1] === undefined) return null;
  const number = Number(match[1]);
  return Number.isSafeInteger(number) && number > 0 ? number : null;
}

/** The pull request a conversation's work is on, as the server records it (its own branch). */
export type ConversationPullRequestClaim = { branch: string; url: string | null };

/**
 * A conversation's pull request in a folder other conversations may share: the folder's live one
 * while it is on the conversation's branch (or is the one it opened), else the last one seen of
 * it, else the one it opened as its URL tells. None without a record: the folder's branch is
 * another conversation's, or nobody's.
 */
export function claimedPullRequest(
  claim: ConversationPullRequestClaim | null | undefined,
  live: PullRequestSummary | null | undefined,
  cached: PullRequestSummary | null | undefined,
): PullRequestSummary | null {
  if (claim === null || claim === undefined) return null;
  const ours = (pullRequest: PullRequestSummary | null | undefined) =>
    pullRequest !== null &&
    pullRequest !== undefined &&
    (pullRequest.headBranch === claim.branch || pullRequest.url === claim.url);
  if (ours(live)) return live ?? null;
  if (ours(cached)) return cached ?? null;
  const number = pullRequestNumberFromUrl(claim.url);
  if (claim.url === null || number === null) return null;
  return {
    url: claim.url,
    number,
    title: `Pull request #${number}`,
    body: '',
    state: 'open',
    baseBranch: '',
    headBranch: claim.branch,
    updatedAt: null,
  };
}

/**
 * How a pull request became a conversation's: it opened it (`created`), a tool call of its turns
 * gave its URL (`printed`), it pushed its branch (`pushed`), the user linked or attached it.
 */
export type ConversationPullRequestOrigin = 'created' | 'printed' | 'pushed' | 'manual';

/** One of a conversation's pull requests, with what its forge says of it (null: unreachable). */
export type ConversationPullRequestBindingView = {
  url: string;
  number: number;
  origin: ConversationPullRequestOrigin;
  branch: string | null;
  pullRequest: PullRequestSummary | null;
};

/**
 * A conversation's pull requests (`/api/local/conversations/:id/pull-requests`): those bound to
 * it, the ones the user removed, and its folder's branch's one it may attach.
 */
export type ConversationPullRequestsView = {
  pullRequests: ConversationPullRequestBindingView[];
  dismissed: string[];
  offer: PullRequestSummary | null;
};

/** A bound pull request as rows show it: the forge's, else what its URL tells. */
export function boundPullRequest(binding: ConversationPullRequestBindingView): PullRequestSummary {
  return (
    binding.pullRequest ?? {
      url: binding.url,
      number: binding.number,
      title: `Pull request #${binding.number}`,
      body: '',
      state: 'open',
      baseBranch: '',
      headBranch: binding.branch ?? '',
      updatedAt: null,
    }
  );
}

/** A pull request bound to a conversation as its record lists it; `dismissed`: the user removed it. */
export type ConversationPullRequestBinding = {
  url: string;
  branch: string | null;
  dismissed: boolean;
};

const sameUrl = (left: string, right: string) =>
  left.replace(/\/+$/, '').toLowerCase() === right.replace(/\/+$/, '').toLowerCase();

/**
 * A conversation's pull request with its bindings: its own (`own`) unless the user removed it,
 * else the last one bound to it, as a summary it is `known` by or as its URL tells.
 */
export function withPullRequestBindings(
  own: PullRequestSummary | null,
  bindings: readonly ConversationPullRequestBinding[] | undefined,
  known: readonly (PullRequestSummary | null | undefined)[] = [],
): PullRequestSummary | null {
  if (bindings === undefined || bindings.length === 0) return own;
  if (
    own !== null &&
    !bindings.some((binding) => binding.dismissed && sameUrl(binding.url, own.url))
  )
    return own;
  const last = bindings.findLast(
    (binding) => !binding.dismissed && forgePullRequestNumber(binding.url) !== null,
  );
  if (last === undefined) return null;
  const found = known.find(
    (entry) => entry !== null && entry !== undefined && sameUrl(entry.url, last.url),
  );
  if (found !== null && found !== undefined) return found;
  return boundPullRequest({
    url: last.url,
    number: forgePullRequestNumber(last.url) ?? 0,
    origin: 'manual',
    branch: last.branch,
    pullRequest: null,
  });
}
